import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { rupee, waLink } from "@/lib/whatsapp";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, MessageCircle, Loader2, Eye, Receipt, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";

const STATUSES = ["Pending Payment", "New", "Confirmed", "Processing", "Packed", "Shipped", "Out for Delivery", "Delivered", "Cancelled", "Refunded"];
const COLOR = { "Pending Payment": "bg-mustard/40 text-earth", New: "bg-mustard/30 text-brown", Delivered: "bg-earth/15 text-earth", Cancelled: "bg-terracotta/20 text-terracotta", Shipped: "bg-forest/15 text-forest" };

export default function AdminOrders() {
  const { settings } = useApp();
  const [orders, setOrders] = useState(null);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState(null);

  const load = () => api.get(`/admin/orders?status=${filter}`).then((r) => setOrders(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = (id, status) => api.put(`/admin/orders/${id}/status`, { status }).then(() => { toast.success("Status updated"); load(); if (view?.id === id) setView({ ...view, status }); });

  const del = (o) => { if (!window.confirm(`Delete order ${o.order_number}? This cannot be undone.`)) return; api.delete(`/admin/orders/${o.id}`).then(() => { toast.success("Order deleted"); if (view?.id === o.id) setView(null); load(); }); };

  const shareInvoice = (o) => {
    const tpl = settings.wa_invoice_template || "Hi {name},\nInvoice for Order #{order} (₹{amount}): {link}";
    const link = `${API}/orders/${o.id}/invoice`;
    const msg = tpl
      .replaceAll("{name}", o.address.full_name || "")
      .replaceAll("{order}", o.order_number || "")
      .replaceAll("{amount}", String(Math.round(o.total || 0)))
      .replaceAll("{link}", link);
    window.open(waLink(o.address.phone, msg), "_blank");
    toast.success("WhatsApp opened — invoice link attached to the message");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight">Orders</h1>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px] rounded-full bg-white border-sage" data-testid="order-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Orders</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
        {!orders ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div> :
          orders.length === 0 ? <p className="text-center py-16 text-muted-foreground">No orders found.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-sand text-brown"><tr>{["Order", "Customer", "Total", "Payment", "Status", "Date", ""].map((h) => <th key={h} className="text-left px-4 py-3 font-500 whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-t border-sage/15" data-testid={`admin-order-${o.id}`}>
                    <td className="px-4 py-3 font-500 text-forest">{o.order_number}</td>
                    <td className="px-4 py-3 text-brown">{o.address.full_name}<div className="text-xs text-muted-foreground">{o.address.phone}</div></td>
                    <td className="px-4 py-3 font-500">{rupee(o.total)}</td>
                    <td className="px-4 py-3 text-xs">{o.payment_method}<div><span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-[11px] font-500 ${o.payment_status === "Paid" ? "bg-earth/15 text-earth" : o.payment_status === "Failed" ? "bg-terracotta/20 text-terracotta" : "bg-mustard/30 text-brown"}`} data-testid={`order-payment-${o.id}`}>{o.payment_status}</span></div></td>
                    <td className="px-4 py-3">
                      <Select value={o.status} onValueChange={(v) => setStatus(o.id, v)}>
                        <SelectTrigger className={`h-8 rounded-full text-xs border-0 w-[150px] ${COLOR[o.status] || "bg-sand text-brown"}`} data-testid={`order-status-${o.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{o.created_at?.slice(0, 10)}</td>
                    <td className="px-4 py-3"><div className="flex gap-1">
                      <button onClick={() => setView(o)} className="p-1.5 hover:bg-sand rounded-lg" data-testid={`view-order-${o.id}`}><Eye className="w-4 h-4 text-forest" /></button>
                      <a href={`${API}/orders/${o.id}/invoice`} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-sand rounded-lg" title="Download invoice"><Download className="w-4 h-4 text-olive" /></a>
                      <button onClick={() => shareInvoice(o)} data-testid={`share-invoice-${o.id}`} title="Share Invoice on WhatsApp" className="p-1.5 hover:bg-sand rounded-lg"><Receipt className="w-4 h-4 text-[#25D366]" /></button>
                      <a href={waLink(o.address.phone, `Hi ${o.address.full_name}, regarding your LIVENEAT order ${o.order_number}.`)} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-sand rounded-lg" title="Message customer"><MessageCircle className="w-4 h-4 text-[#25D366]" /></a>
                      <button onClick={() => del(o)} data-testid={`delete-order-${o.id}`} className="p-1.5 hover:bg-sand rounded-lg" title="Delete order"><Trash2 className="w-4 h-4 text-terracotta" /></button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="bg-cream max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-forest">{view?.order_number}</DialogTitle></DialogHeader>
          {view && (
            <div className="space-y-4 text-sm">
              <div className="bg-white rounded-xl p-4"><b className="text-forest">Ship to:</b><br />{view.address.full_name}, {view.address.phone}<br />{view.address.line1} {view.address.line2}, {view.address.city}, {view.address.state} - {view.address.pincode}</div>
              <div className="bg-white rounded-xl p-4 space-y-2">
                {view.items.map((i, idx) => <div key={idx} className="flex justify-between"><span>{i.name} × {i.qty}</span><span>{rupee(i.price * i.qty)}</span></div>)}
                <div className="border-t border-sage/20 pt-2 flex justify-between font-600 text-forest"><span>Total</span><span>{rupee(view.total)}</span></div>
              </div>
              <div className="flex gap-2">
                <a href={`${API}/orders/${view.id}/invoice`} target="_blank" rel="noreferrer" className="flex-1"><Button variant="outline" className="w-full rounded-full border-forest text-forest"><Download className="w-4 h-4 mr-1.5" /> Invoice PDF</Button></a>
                <Button onClick={() => shareInvoice(view)} data-testid="share-invoice-detail" className="flex-1 rounded-full bg-[#25D366] hover:bg-[#1eb85a] text-white"><MessageCircle className="w-4 h-4 mr-1.5" /> Share Invoice on WhatsApp</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
